'use client';

import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useUser } from '@clerk/nextjs';
import {
  SubscriptionTier,
  SubscriptionMethod,
  SubscriptionState,
  FREE_TIER_ASSET_LIMIT,
  PAID_TIER_ASSET_LIMIT,
} from '@/types/subscription';

interface SubscriptionContextValue extends SubscriptionState {
  isLoading: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useUser();

  const value = useMemo<SubscriptionContextValue>(() => {
    const tier = (user?.publicMetadata?.tier as SubscriptionTier) || 'free';
    const isPaid = tier === 'paid';
    const subscriptionMethod = user?.publicMetadata?.subscriptionMethod as SubscriptionMethod | undefined;

    const cancelAtPeriodEnd = user?.publicMetadata?.cancelAtPeriodEnd as boolean | undefined;
    const currentPeriodEndStr = user?.publicMetadata?.currentPeriodEnd as string | undefined;
    const currentPeriodEnd = currentPeriodEndStr ? new Date(currentPeriodEndStr) : undefined;
    const billingInterval = user?.publicMetadata?.billingInterval as 'monthly' | 'yearly' | undefined;

    return {
      tier,
      subscriptionMethod,
      stripeCustomerId: user?.publicMetadata?.stripeCustomerId as string | undefined,
      assetLimit: isPaid ? PAID_TIER_ASSET_LIMIT : FREE_TIER_ASSET_LIMIT,
      paymentFailed: user?.publicMetadata?.paymentFailed as boolean | undefined,
      cancelAtPeriodEnd,
      currentPeriodEnd,
      billingInterval,
      isLoading: !isLoaded,
    };
  }, [user, isLoaded]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
}
